import React from 'react';
import { format, parseISO } from 'date-fns';

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatDate(dateStr, fmt) {
  if (!dateStr) return '';
  try {
    const d = typeof dateStr === 'string' ? parseISO(dateStr) : dateStr;
    if (fmt === 'DD/MM/YYYY') return format(d, 'dd/MM/yyyy');
    if (fmt === 'YYYY-MM-DD') return format(d, 'yyyy-MM-dd');
    if (fmt === 'DD-MMM-YYYY') return `${format(d, 'dd')}-${MONTH_ABBR[d.getMonth()]}-${format(d, 'yyyy')}`;
    return format(d, 'MM/dd/yyyy');
  } catch { return String(dateStr); }
}

const BANNER_COLORS = {
  black:  { background: '#111', color: '#fff' },
  blue:   { background: '#0d6efd', color: '#fff' },
  red:    { background: '#dc3545', color: '#fff' },
  green:  { background: '#198754', color: '#fff' },
  orange: { background: '#fd7e14', color: '#fff' },
};

export default function PalletLabel({ pallet, bol, template, companyName, companyAddress, logoUrl, isLastPage }) {
  const t = template || {};
  const bannerStyle = BANNER_COLORS[t.storageBannerColor] || BANNER_COLORS.black;
  const dateLabel = t.dateLabel || 'Expiration';
  const casesLabel = t.casesLabel || 'CASES ON PALLET';
  const showLogo = t.showLogo !== false;
  const showMfg = t.showManufacturedBy !== false;
  const isLetter = t.labelSize === 'letter';

  const W = isLetter ? '11in' : '6in';
  const H = isLetter ? '8.5in' : '4in';
  const PAD = isLetter ? '0.45in' : '0.13in';
  const s = isLetter ? 1.8 : 1;

  const expirationDisplay = pallet.expirationDate ? formatDate(pallet.expirationDate, t.dateFormat) : '';
  const poDisplay = bol?.customerPO || bol?.poNumber || '';

  // Label column width — wide enough for longest label
  const LABEL_W = s * 78;

  return (
    <div style={{
      width: W, height: H, padding: PAD, boxSizing: 'border-box',
      fontFamily: 'Arial, Helvetica, sans-serif',
      border: '2px solid #000',
      display: 'flex', flexDirection: 'column',
      background: '#fff',
      pageBreakAfter: isLastPage ? 'auto' : 'always', pageBreakInside: 'avoid',
      overflow: 'hidden',
    }}>

      {/* Banner */}
      {t.storageBanner && (
        <div style={{
          ...bannerStyle,
          margin: `calc(${PAD} * -1) calc(${PAD} * -1) ${s * 10}px`,
          padding: `${s * 7}px ${s * 14}px`,
          fontWeight: 'bold', fontSize: s * 14,
          textAlign: 'center', letterSpacing: 1,
          textTransform: 'uppercase', flexShrink: 0,
        }}>
          {t.storageBanner}
        </div>
      )}

      {/* PALLET X / Y — centered, very large */}
      <div style={{ textAlign: 'center', marginBottom: s * 2, flexShrink: 0 }}>
        <div style={{ fontSize: s * 38, fontWeight: 'bold', lineHeight: 1, letterSpacing: 1 }}>
          PALLET {pallet.palletNumber} / {pallet.totalPallets}
        </div>
        <div style={{ fontSize: s * 12, color: '#555', marginTop: s * 1 }}>
          ID: {pallet.palletId}
        </div>
      </div>

      {/* Field rows */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: s * 5 }}>
        <FieldLine label="PO #"       value={poDisplay}                            labelW={LABEL_W * 1.5} s={s * 1.5} />
        <FieldLine label="Lot(s)"     value={pallet.lotCodes?.join(', ') || ''}    labelW={LABEL_W * 1.5} s={s * 1.5} />
        <FieldLine label="Product"    value={pallet.products?.join(' / ') || ''}   labelW={LABEL_W * 1.5} s={s * 1.5} />
        <FieldLine label={dateLabel}  value={expirationDisplay}                    labelW={LABEL_W * 1.5} s={s * 1.5} bold />
      </div>

      {/* Bottom: mfg | logo (center) | cases */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        alignItems: 'flex-end',
        gap: s * 6,
        marginTop: s * 4,
        flexShrink: 0,
      }}>
        {/* Left: Manufactured by */}
        <div style={{ fontSize: s * 10, color: '#333', lineHeight: 1.4 }}>
          {showMfg && (
            <>
              <div style={{ fontWeight: 'bold', fontSize: s * 11, marginBottom: 2 }}>Manufactured by:</div>
              <div style={{ fontWeight: 'bold' }}>{companyName}</div>
              <div>{companyAddress}</div>
            </>
          )}
        </div>

        {/* Center: Logo */}
        <div style={{ textAlign: 'center' }}>
          {showLogo && logoUrl && (
            <img src={logoUrl} alt="logo"
              style={{ maxHeight: s * 50, maxWidth: s * 130, objectFit: 'contain', display: 'block' }} />
          )}
        </div>

        {/* Right: Cases box */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <div style={{
            border: '2.5px solid #000', borderRadius: 4,
            padding: `${s * 5}px ${s * 12}px`,
            textAlign: 'center', minWidth: s * 76,
          }}>
            <div style={{ fontSize: s * 8, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
              {casesLabel}
            </div>
            <div style={{ fontSize: s * 32, fontWeight: 'bold', lineHeight: 1 }}>
              {pallet.cases ?? 0}
            </div>
          </div>
        </div>
      </div>

      {t.customFooterNote && (
        <div style={{ fontSize: s * 8, color: '#666', marginTop: s * 4, textAlign: 'center', flexShrink: 0 }}>
          {t.customFooterNote}
        </div>
      )}
    </div>
  );
}

function FieldLine({ label, value, labelW, s, bold }) {
  const text = String(value || '');
  const fontScale = text.length > 43 ? Math.max(0.6, 43 / text.length) : 1;
  const baseSize = s * (bold ? 16 : 14);

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: s * 6 }}>
      {/* Fixed-width label */}
      <div style={{
        flexShrink: 0, width: labelW,
        fontSize: s * 13, fontWeight: '600', color: '#333',
        paddingBottom: 2,
        whiteSpace: 'nowrap',
      }}>
        {label}:
      </div>
      {/* Value with underline that stretches full remaining width */}
      <div style={{
        flex: 1,
        borderBottom: '1.5px solid #999',
        paddingBottom: 2,
        fontSize: baseSize * fontScale,
        fontWeight: bold ? 'bold' : '500',
        minHeight: s * 20,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}>
        {value}
      </div>
    </div>
  );
}
